import { useQuery } from "@apollo/client";
import { ColDef, GridOptions, SortDirection } from "ag-grid-enterprise";
import { DateFilter, DateUtils } from "utils/dateutils";
import { AgGridReact } from "ag-grid-react";
import { Button } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import {
  IdLinkComponent,
  UpcomingSubmittalReportDetailsCellRenderer,
  dueDateCellRenderer,
  dummyColumnDef2,
  riskCellRenderer,
  specSectionLabel,
  specSectionRenderer,
  submittalDueDateHeaderComponent,
  truncatedTextCellRenderer,
  userOrgCellRenderer
} from "components/cell-renders";
import { ProjectContext, ProjectSettingType } from "context/ProjectProvider";
import {
  useIntegrationProjectParticipants,
  useProjectParticipants
} from "hooks/project-participants";
import { updateSubmittalList } from "pages/submittals-list/helpers";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { QUERY_SUBMITTAL_TYPES } from "services/graphQL/queries";

import {
  downloadStringAsCSVFile,
  isProjectInIntegrationMode
} from "utils/utils";
import { useCiqFeatureSubmittalRiskReportList } from "pages/reports-menu/project-reports/risk-report/hooks/risk-report";
import { submittalSequenceComparator } from "pages/submittals-log-page/utils";
import { useHistory, useParams } from "react-router";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { submittalStatusCellRendererV2Report } from "entity-app/shared-components/log-render-components/cell-renders";
import {
  comparatorWithEmptyAtBottom,
  riskLevelComparator
} from "utils/grid-utils";
import { SubmittalStatusToStrMap } from "constants/index";
import { getSubmittalFormattedValues } from "./export-csv-util";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";

type ReportFilterType = {
  filter: number;
  groupby: string;
  sortBy: string;
};

type TUpcomingGroupRow = {
  name: string;
  id: string;
  callRecords: Array<any>;
};

function isDueOnOrBeforeFilterDate(dueDate: any, dateForFilter: any) {
  return (
    !!dueDate && DateUtils.dateTimeObj(dueDate).diff(dateForFilter, "days") <= 0
  );
}

function resolveSubmittalGroupName(
  data: any,
  groupByItem: any,
  groupby: string
) {
  if (data[groupByItem.name]) return data[groupByItem.name];

  if (data[groupByItem.firstName]) {
    return `${data[groupByItem.firstName]} ${data[groupByItem.lastName]}`;
  }

  return groupby === "BIC_GroupBy"
    ? "No Ball in Court"
    : "Unassigned Responsible Contractor";
}

function groupUpcomingSubmittals(
  list: Array<any> | undefined,
  groupByItem: any,
  groupby: string,
  dateForFilter: any
) {
  const masterD: Array<TUpcomingGroupRow> = [];
  let submittalCount = 0;

  list?.forEach((data: any) => {
    if (
      !isDueOnOrBeforeFilterDate(
        data.date_block_submittals[0]?.due_date,
        dateForFilter
      )
    ) {
      return;
    }

    ++submittalCount;
    const id = data[groupByItem.id]
      ? data[groupByItem.id]
      : data[groupByItem.name];

    const existing = masterD.find((x) => x.id === id);
    if (existing) {
      existing.callRecords.push(data);
      return;
    }

    masterD.push({
      name: resolveSubmittalGroupName(data, groupByItem, groupby),
      id,
      callRecords: [data]
    });
  });

  return { masterD, submittalCount };
}

function UpcomingSubmittalsComponent(props: {
  reportFilter: any;
  setOpenFilterForm: any;
  projectDetails: ProjectSettingType | undefined;
}) {
  const history = useHistory();
  const { projectId } = useParams() as any;

  const { reportFilter, setOpenFilterForm, projectDetails } = props;
  const [numberOfSubmittals, setNumberOfSubmittals] = useState<number>(0);
  const [gridSearchtext, setGridSearchtext] = useState("");
  const [masterData, setMasterData] =
    useState<Array<{ name: string; id: string; callRecords: Array<any> }>>();

  const [expandedRowCount, setExpandedRowCount] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [isGridReady, setGridReady] = useState(false);

  const gridRef = useRef<AgGridReact<any>>(null);

  const {
    gqlClientForProject,
    isProjectArchived,
    columnHeaders: { submittalHeaderMap }
  } = useContext(ProjectContext);

  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const { data: INTGProjectParticipants } =
    useIntegrationProjectParticipants(!isIntegrationMode);

  const { projectParticipants: WFProjectParticipants } =
    useProjectParticipants(isIntegrationMode);

  const projectParticipants = isIntegrationMode
    ? INTGProjectParticipants
    : WFProjectParticipants;

  const { gridData } = useCiqFeatureSubmittalRiskReportList({});

  const { data: submittalTypes } = useQuery(QUERY_SUBMITTAL_TYPES, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const updateGridView = (e: ReportFilterType, list: Array<any>) => {
    const dateForFilter = DateUtils.dateTimeObj(e.filter);

    const groupByItem: any =
      e.groupby === "BIC_GroupBy"
        ? {
            name: "assignee_unregistered",
            id: "assignee_user_id",
            firstName: "assignee_first_name",
            lastName: "assignee_last_name"
          }
        : {
            name: "responsible_contractor_name",
            id: "responsible_contractor_id"
          };

    const { masterD, submittalCount } = groupUpcomingSubmittals(
      list,
      groupByItem,
      e.groupby,
      dateForFilter
    );

    setNumberOfSubmittals(submittalCount);

    setMasterData(masterD.sort((a, b) => a.name.localeCompare(b.name)));
    setTotalNumberOfRows(submittalCount + masterD.length);
  };

  const submittalListTableData: Array<any> = useMemo(() => {
    if (!gridData?.submittals || !projectParticipants.allProjectUsersMap)
      return null;

    const updatedSubmittalList = updateSubmittalList(
      gridData?.submittals,
      projectParticipants
    );

    if (reportFilter) updateGridView(reportFilter, updatedSubmittalList);

    return updatedSubmittalList;
  }, [projectParticipants, reportFilter, gridData?.submittals]);

  const expandCollapseAll = useCallback(() => {
    const isExpand = expandedRowCount !== totalNumberOfRows; // tmpExpandedItemIds.length > 0;

    gridRef?.current?.api?.forEachNode((node) => {
      node.setExpanded(isExpand);
    });

    if (isExpand) setExpandedRowCount(totalNumberOfRows);
    else setExpandedRowCount(0);
    gridRef?.current?.api?.onGroupExpandedOrCollapsed();
  }, [expandedRowCount, totalNumberOfRows]);

  const defaultColDef = useMemo(() => {
    return {
      sortingOrder: ["asc", "desc"] as SortDirection[],
      suppressColumnsToolPanel: true,
      flex: 1,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 200
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        cellRenderer: "agGroupCellRenderer",
        valueGetter: ({ data }) => `${data.name} (${data.callRecords.length})`
      }
    ],
    []
  );

  const detailCellRenderer = useMemo(() => {
    return UpcomingSubmittalReportDetailsCellRenderer;
  }, []);

  const typesMap = useMemo(() => {
    if (submittalTypes) {
      const tmpTypesMap: any = {};
      submittalTypes.configurations_value.forEach((type: any) => {
        tmpTypesMap[type.id] = type;
      });
      return tmpTypesMap;
    }
    return null;
  }, [submittalTypes]);

  const statusesMap: any = useMemo(() => {
    const tmpStatusesMap: any = {};

    Object.keys(SubmittalStatusToStrMap).forEach((keyStatusId: any) => {
      tmpStatusesMap[keyStatusId] = {
        name: SubmittalStatusToStrMap[keyStatusId]
      };
    });
    return tmpStatusesMap;
  }, []);

  const onUpdateExpandedRow = () => {
    let count = 0;
    gridRef.current?.api?.forEachNode((node) => {
      if (node.expanded) count = 1 + count;
    });
    gridRef.current?.api?.forEachDetailGridInfo((gridInfo) => {
      gridInfo.api?.forEachNode((node) => {
        if (node.expanded) count = 1 + count;
      });
    });
    setExpandedRowCount(count);
  };

  /*
   * The chevron used to be the only expand/collapse affordance on a master row.
   * Toggle from anywhere on the row instead, with two guards:
   *   - only master rows; the detail row rendered underneath belongs to this
   *     grid too, so clicks inside the nested grid would otherwise collapse it;
   *   - skip clicks that landed on the chevron, which AG Grid already handles —
   *     both handlers fire on the same click and would cancel each other out.
   */
  const onMasterRowClicked = useCallback((event: any) => {
    if (!event.node?.master) return;
    const target = event.event?.target as HTMLElement | undefined;
    if (target?.closest?.(".ag-group-expanded, .ag-group-contracted")) return;
    event.node.setExpanded(!event.node.expanded);
  }, []);

  const getMasterRowStyle = useCallback(
    (params: any) => (params.node?.master ? { cursor: "pointer" } : undefined),
    []
  );

  const gridContext: any = useMemo(() => {
    return {
      projectDetails,
      typesMap,
      statusesMap,
      isIntegrationMode,
      projectParticipants,
      submittalHeaderMap
    };
  }, [
    isIntegrationMode,
    projectDetails,
    projectParticipants,
    statusesMap,
    typesMap,
    submittalHeaderMap
  ]);

  const detailCellRendererParams = useMemo<{
    detailGridOptions: GridOptions;
  }>(() => {
    return {
      // provide the Grid Options to use on the Detail Grid
      detailGridOptions: {
        context: gridContext,
        masterDetail: true,
        detailCellRenderer,
        detailRowAutoHeight: true,
        headerHeight: 36,
        rowHeight: 50,
        groupDefaultExpanded: -1,
        animateRows: false,
        defaultColDef: {
          sortable: true,
          filter: "agSetColumnFilter",
          resizable: true,
          comparator: comparatorWithEmptyAtBottom
        },
        onGridReady(gridApi) {
          // Unreachable: this same useMemo dereferences reportFilter.groupby (L428/L475)
          // while building detailGridOptions, so a falsy reportFilter throws before this
          // closure can ever run — by the time onGridReady fires, reportFilter is truthy.
          /* v8 ignore next */
          if (!reportFilter) return;
          gridApi.api.applyColumnState({
            state: [
              {
                colId: reportFilter.sortBy,
                sort: "asc"
              }
            ]
          });
          gridApi.api?.sizeColumnsToFit();
        },
        onRowGroupOpened() {
          onUpdateExpandedRow();
        },
        columnDefs: [
          dummyColumnDef2,
          {
            colId: "submittal_id",
            field: "submittal_id",
            headerName: submittalHeaderMap?.submittal_id || "",
            headerTooltip: submittalHeaderMap?.submittal_id || "",
            width: 150,
            minWidth: 80,
            maxWidth: 200,
            cellRenderer: "agGroupCellRenderer",
            cellRendererParams: {
              innerRenderer: IdLinkComponent,
              projectId: projectDetails?.id
            },
            comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) =>
              submittalSequenceComparator(
                valueA,
                valueB,
                nodeA,
                nodeB,
                projectDetails?.spec_section
              )
          },
          {
            colId: "custom_spec_section",
            headerName: submittalHeaderMap?.spec_no || "",
            headerTooltip: submittalHeaderMap?.spec_no || "",
            cellRenderer: specSectionRenderer,
            cellRendererParams: {
              specNoField: "spec_no",
              specNameField: "spec_name"
            },
            valueGetter: (params: any) => {
              try {
                if (!params.data) return "";
                const specSectionStr = specSectionLabel({
                  specNumber: params.data.spec_no,
                  specName: params.data.spec_name
                });

                return specSectionStr;
              } catch {
                return "";
              }
            }
          },
          {
            colId: "title",
            field: "title",
            headerName: submittalHeaderMap?.title || "",
            headerTooltip: submittalHeaderMap?.title || "",
            cellRenderer: truncatedTextCellRenderer
          },
          {
            colId: "type",
            field: "type",
            headerName: submittalHeaderMap?.type || "",
            headerTooltip: submittalHeaderMap?.type || "",
            keyCreator: (params: any) => params.value,
            getQuickFilterText: (params: any) => params.value,
            cellRenderer: truncatedTextCellRenderer
          },
          {
            colId: "status",
            field: "workflow_status",
            headerName: submittalHeaderMap?.workflow_status || "",
            headerTooltip: submittalHeaderMap?.workflow_status || "",
            cellRenderer: (params: any) =>
              submittalStatusCellRendererV2Report(params),
            cellRendererParams: {
              dataMapVar: "statusesMap",
              valueVar: "id",
              labelVar: "name"
            },

            keyCreator: (params: any) => {
              try {
                return params.context.statusesMap[params.value].name;
              } catch {
                return "";
              }
            }
          },
          {
            colId: "assignee",
            headerName: submittalHeaderMap?.assignee || "",
            headerTooltip: submittalHeaderMap?.assignee || "",
            cellRenderer: (params: any) => {
              if (!params.data) return null;
              const data = userOrgCellRenderer(params);
              return data.cellMarkup;
            },
            cellRendererParams: {
              userInput: {
                id: "assignee_user_id",
                firstName: "assignee_first_name",
                lastName: "assignee_last_name",
                org: "assignee_org",
                unregistered_user: "assignee_unregistered",
                unregistered_user_org: "assignee_unregistered_org",
                statusKey: "assignee_status"
              }
            },
            valueGetter: (params: any) => {
              if (!params.data) return null;
              const data = userOrgCellRenderer(params);
              return data.fullName;
            },
            tooltipValueGetter: (params: any) => {
              if (!params.data) return null;
              const data = userOrgCellRenderer(params);
              return `${data.fullName} - ${data.userOrg}`;
            },
            hide: reportFilter.groupby === "BIC_GroupBy"
          },
          {
            colId: "dueDate",
            headerName: submittalHeaderMap?.due_date || "",
            headerTooltip: submittalHeaderMap?.due_date || "",
            headerComponentParams: submittalDueDateHeaderComponent,
            cellRenderer: dueDateCellRenderer,
            comparator: DateFilter.comparator,
            valueGetter: ({ data }) => {
              if (!data || data.date_block_submittals.length === 0) return "";
              return data.date_block_submittals[0]?.due_date
                ? DateUtils.format(data.date_block_submittals[0]?.due_date)
                : "";
            },
            tooltipValueGetter: (params) => params.value,
            filter: CustomDateSetFilter
          },
          {
            colId: "risk_level",
            field: "risk_level",
            headerName: "Risk",
            hide: true,
            comparator: riskLevelComparator,
            cellRenderer: riskCellRenderer
          },
          {
            colId: "risk_assessment",
            headerName: "Risk Assessment",
            hide: true
          },
          {
            colId: "linked_materials",
            field: "submittal_material_links",
            headerName: "Impacted Materials",
            cellDataType: false,
            hide: true
          },
          {
            colId: "linked_tasks",
            field: "submittal_schedule_links",
            headerName: "Impacted Activities",
            cellDataType: false,
            hide: true
          },
          {
            colId: "BIC_GroupBy",
            headerName: submittalHeaderMap?.responsible_contractor || "",
            headerTooltip: submittalHeaderMap?.responsible_contractor || "",
            rowGroup: false,
            hide: reportFilter.groupby === "RC_GroupBy",
            valueGetter: (param) => param.data.responsible_contractor_name,

            cellRenderer: ({ value }: any) => (
              <div className="font-medium">{value}</div>
            )
          }
        ] as ColDef[]
      },

      // get the rows for each Detail Grid
      getDetailRowData: (params: any) => {
        params.successCallback(params.data.callRecords);
      }
    };
  }, [
    detailCellRenderer,
    gridContext,
    projectDetails,
    reportFilter,
    submittalHeaderMap
  ]);

  useEffect(() => {
    gridRef.current?.api?.forEachDetailGridInfo((gridInfo) => {
      gridInfo.api?.setGridOption("quickFilterText", gridSearchtext);
    });
    gridRef.current?.api?.onFilterChanged();
  }, [gridSearchtext]);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef?.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isGridReady]);

  useEffect(() => {
    if (gridData?.submittals) {
      setTimeout(() => {
        gridRef.current?.api.refreshCells({
          force: true,
          columns: ["assignee"]
        });
      }, 0);
    }
  }, [gridData?.submittals]);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [gridData?.submittals, projectParticipants]);

  const getParamsForCSVExport = useCallback(
    ({ skipColumnHeaders, detailGridInfo, firstTableParams }: any) => {
      const additionalColumns = [
        "assignee",
        "BIC_GroupBy",
        "risk_level",
        "risk_assessment",
        "linked_materials",
        "linked_tasks"
      ];

      const allgridCols = detailGridInfo?.api?.getAllGridColumns();

      const columnKeysData = firstTableParams?.columnKeys
        ? firstTableParams?.columnKeys
        : allgridCols
            ?.filter((col: any) => {
              const colId = col.getColId();
              return col.isVisible() || additionalColumns?.includes(colId);
            })
            .map((col: any) => col.getColId());

      return {
        columnKeys: columnKeysData,
        processCellCallback(params: any): string {
          return getSubmittalFormattedValues(params);
        },
        skipColumnHeaders
      };
    },
    []
  );

  const exportLogAsCSV = useCallback(() => {
    let csvOpString = "";
    let firstTableParams: any = null;
    gridRef.current?.api?.forEachDetailGridInfo(
      (detailGridInfo, index: number) => {
        const getDataParams = getParamsForCSVExport({
          skipColumnHeaders: index > 0,
          detailGridInfo,
          firstTableParams
        });

        if (index === 0) firstTableParams = getDataParams;

        const gridDataAsCsv = detailGridInfo?.api?.getDataAsCsv(getDataParams);
        csvOpString += index > 0 ? `\n${gridDataAsCsv}` : gridDataAsCsv;
      }
    );

    const filename = `Upcoming Submittals_${projectDetails?.name}.csv`;

    downloadStringAsCSVFile(csvOpString, filename);
    saveAuditLogEvent({
      eventType: "UPCOMING_SUBMITTALS_REPORT_EXPORT_CSV",
      featureType: "SUBMITTALS",
      filename
    });
  }, [saveAuditLogEvent, getParamsForCSVExport, projectDetails?.name]);

  const gotoReports = () => {
    history.push(`/project/${projectId}/reports/project-reports`);
  };

  return (
    <div
      className="flex flex-col h-full w-full"
      data-testid="upcoming-submittals-page"
    >
      <div className="px-6 py-6 flex justify-between items-center">
        <div className="font-medium flex items-center">
          <ArrowLeftOutlined
            className="text-black"
            onClick={gotoReports}
            data-testid="upcoming-submittals-back"
          />
          <div className="text-xl font-semibold pl-4 pr-2">
            Upcoming Submittals
          </div>
          <div
            className="flex h-fit flex-wrap items-center border border-solid border-ciq-gray-300 rounded-xl py-1 px-2 text-black text-opacity-50 text-xs font-normal"
            data-testid="upcoming-submittals-count"
          >
            {numberOfSubmittals}
          </div>
        </div>
        <div className="flex space-x-3 items-center">
          <CiqAgSearch
            gridRef={gridRef}
            placeholder="Search Submittals"
            data-testid="upcoming-submittals-search"
            disabled={!submittalListTableData?.length}
            onSearch={(text) => {
              setGridSearchtext(text);
            }}
          />

          <Button
            onClick={() => {
              expandCollapseAll();
            }}
            className="w-[110px]"
            disabled={!submittalListTableData?.length}
            data-testid="upcoming-submittals-expand-toggle"
          >
            {expandedRowCount === totalNumberOfRows ? (
              <span>Collapse All</span>
            ) : (
              <span>Expand All</span>
            )}
          </Button>

          <Button
            onClick={() => {
              gridRef?.current?.api?.forEachNode((node) => {
                node.setExpanded(true);
              });

              setTimeout(() => {
                exportLogAsCSV();
              }, 500);
            }}
            disabled={
              !submittalListTableData ||
              !submittalListTableData.length ||
              !!isProjectArchived
            }
            data-testid="upcoming-submittals-export-csv"
          >
            Export CSV
          </Button>
          <Button
            type="primary"
            onClick={() => setOpenFilterForm(true)}
            data-testid="upcoming-submittals-run-new-report"
          >
            Run New Report
          </Button>
        </div>
      </div>
      <div className="grow ag-theme-alpine">
        <AgGridReact
          ref={gridRef}
          className="ag-theme-alpine"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          headerHeight={0}
          rowData={masterData}
          animateRows={false}
          suppressContextMenu
          onGridReady={() => {
            setGridReady(true);
          }}
          masterDetail
          detailCellRendererParams={detailCellRendererParams}
          onRowClicked={onMasterRowClicked}
          getRowStyle={getMasterRowStyle}
          onRowGroupOpened={() => {
            onUpdateExpandedRow();
          }}
          keepDetailRows
          detailRowAutoHeight
          isExternalFilterPresent={() => {
            return true;
          }}
          doesExternalFilterPass={(rowNode: any) => {
            const { detailNode } = rowNode;
            if (detailNode && detailNode.detailGridInfo) {
              const displayedCount =
                detailNode.detailGridInfo?.api?.getDisplayedRowCount();
              if (displayedCount === 0) return false;
            }
            return true;
          }}
          tooltipShowMode="whenTruncated"
        />
      </div>
    </div>
  );
}

export default UpcomingSubmittalsComponent;
